import {Injectable, OnDestroy} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {filter, first, takeUntil} from 'rxjs/operators';
import {MxGridActionService} from './mx-grid-actions.service';
import {MxGridCollectionService} from './mx-grid-collection.service';
import {MxGridConfigStorageService} from './mx-grid-config-storage.service';
import {MxGridCustomizerEvent} from './mx-grid-customizer-event';
import {GridColumn} from './grid-column';
import {SelectionType} from './selection-type.model';


export interface GridColumnService {

    readonly columns: ReadonlyArray<GridColumn>;

    readonly visibleColumns: ReadonlyArray<string>;

    readonly visibleColumns$: Observable<string[]>;

    setColumns(columns: Array<GridColumn>, updateSelectedColumns?: boolean): void;

    columnsChanged(): Observable<GridColumnService>;

    updateColumns(columns: Array<string>): void;

    changeColumnLabel(field: string, label: string);

}

@Injectable()
export class MxGridColumnService implements GridColumnService, OnDestroy {

    private _columnsChanged$$: Subject<GridColumnService> = new Subject<GridColumnService>();
    private _destroy$$: Subject<null> = new Subject<null>();

    /** Latest copy of columns */
    private _columns: Array<GridColumn> = [];
    private _needHideColumns: Array<string>;

    get columns(): ReadonlyArray<GridColumn> {
        return this._columns;
    }

    get visibleColumns(): ReadonlyArray<string> {
        return this.columnsSelectControl.value;
    }

    get visibleColumns$(): Observable<string[]> {
        return this.columnsSelectControl.valueChanges;
    }

    /** Columns by name */
    // TODO change to by prop
    private _columnsAllByName: Readonly<Map<string, GridColumn>> = new Map();

    get columnsAllByName():  Readonly<Map<string, GridColumn>> {
        return this._columnsAllByName;
    }

    // TODO Rename to showColumns
    private _columnsSelectOptions: ReadonlyArray<{ id, name }> = [];

    get columnsSelectOptions(): ReadonlyArray<{ id, name }> {
        return this._columnsSelectOptions.slice();
    }

    private get rows(): ReadonlyArray<any> {
        return this.collectionService ? this.collectionService.rows : [];
    }

    private _selectionMode: SelectionType = 'multi';

    public get selectionMode(): SelectionType {
        return this._selectionMode;
    }

    private _allowReorder: boolean = false;

    public get allowReorder(): boolean {
        return this._allowReorder;
    }

    private _hideEmptyColumn: boolean = false;
    private _hideUndefinedColumn: boolean = false;

    // TODO move back to grid control
    columnsSelectControl: FormControl = new FormControl();

    constructor(
        private actionService: MxGridActionService,
        private collectionService: MxGridCollectionService,
        private storage: MxGridConfigStorageService
    ) {
        this.columnsSelectControl.setValue([]);
    }

    ngOnDestroy(): void {
        this._columnsChanged$$.complete();
        this._destroy$$.next(null);
    }

    setUp(event: MxGridCustomizerEvent): void {

        this._selectionMode = event.gridProps.selectionMode;
        this._allowReorder = event.gridProps.allowReorder;
        this._hideEmptyColumn = event.gridProps.hideEmptyColumn;
        this._hideUndefinedColumn = event.gridProps.hideUndefinedColumn;

        if (event.columnsSource$) {
            event.columnsSource$
                .pipe(first())
                .subscribe
                ((columns) => {
                        this.setColumns(columns);
                    }
                );
        }

        this.collectionService.rowsChanged()
            .pipe(takeUntil(this._destroy$$))
            .subscribe(
                () => this.updateState());

        this.columnsSelectControl.valueChanges
            .pipe(
                takeUntil(this._destroy$$),
                filter(() => this.columnsSelectControl.dirty)
            )
            .subscribe(value => {
                this.storage.saveColumns(value);
            });

    }

    setColumns(columns: Array<GridColumn>, updateSelectedColumns?: boolean): void {
        this.updateState(columns, updateSelectedColumns);
    }

    columnsChanged(): Observable<GridColumnService> {
        return this._columnsChanged$$.asObservable();
    }

    private updateState(columns: Array<GridColumn> = this._columns || [], updateSelectedColumns: boolean = false): void {
        this._columns = columns || [];
        this._columnsAllByName =
            columns.reduce((map, curr) => map.set(curr.name, curr), new Map<string, GridColumn>());

        this._columnsSelectOptions = columns.map(col => {
            return {id: col.prop, name: col.name};
        });
        let needHideColumn = this.rows.length === 0 || (!this._hideEmptyColumn && !this._hideUndefinedColumn) ? [] :
            columns.filter((col) => {
                return ((this._hideEmptyColumn && typeof this.rows[0][col.prop] === 'undefined')
                    || (this._hideUndefinedColumn && this.rows.every(cols => typeof cols[col.prop] === 'undefined')))
                    && col.isThumbnail !== true;
            }).map(col => col.prop);
        let needShowColumn = this._needHideColumns ? this._needHideColumns.filter(col => !needHideColumn.includes(col)) : [];

        const columnsData: Set<string> = this.storage.getColumnsData();
        if (!!columnsData) {
            columns.forEach(value => {
                if (!columnsData.has(value.prop) && !needHideColumn.includes(value.prop)) {
                    value.hidden = true;
                } else if (columnsData.has(value.prop) && !needShowColumn.includes(value.prop)) {
                    value.hidden = false;
                }
            });
        }

        this._needHideColumns = needHideColumn;
        let visibleColumns: Array<string> = this.columnsSelectControl.value;
        if (this.columnsSelectControl.value.length === 0 || updateSelectedColumns) {
            visibleColumns = columns.filter(col => col.hidden !== true).map(col => col.prop);
        }
        let toShow = [...visibleColumns.filter(col => needHideColumn.indexOf(col) < 0), ...needShowColumn];
        this.columnsSelectControl.setValue(columns.filter(value => toShow.includes(value.prop)).map(value => value.prop));
        this._columnsChanged$$.next(this);
    }

    updateColumns(columns: Array<string>): void {
        this.columnsSelectControl.setValue(columns);
    }

    changeColumnLabel(field: string, label: string): void {
        this._columns.forEach((column) => {
                if (column.prop === field) {
                    column.setLabel(label);
                }
            }
        );
    }

}

